import React from 'react'

import { Button as ShadcnButton } from 'src/components/ui/button'
import { LoadingOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import type { ButtonProps } from './antd-props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Button` seam. `./Button.tsx` is the
 * dispatcher that chooses between this and antd's own Button; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Ported verbatim from the `update-design` reference branch's
 * `src/antd/Button.tsx` (its Phase 2 slice 11), except for this header: the
 * class-name rule below is a LIVE constraint on this branch, not the
 * reference's historical note, because antd is still installed here.
 *
 * The EXTERNAL API is antd's own `ButtonProps`, so the ~600 call sites that
 * already import `Button` from `src/antd` switch with no edit at all.
 *
 * antd → shadcn prop mapping:
 *   type:    primary → default, text → ghost, link → link,
 *            dashed/default → outline
 *   danger:  destructive (filled) — but text/link + danger stays a red *ghost*
 *            so icon-only delete buttons keep their borderless red look
 *   size:    large → lg, small → sm, middle → default; icon-only → icon*
 *   loading: spinner (antd LoadingOutlined) + disabled, as antd does
 *   icon:    rendered before the label. The icon keeps its antd role="img"
 *            aria-label, so an icon passed here DOES join the button's
 *            accessible name — the semantic button kit
 *            (src/storybook/components/Buttons) therefore marks its own icons
 *            `aria-hidden` and names its icon-only buttons with an explicit
 *            `aria-label`, so a kit button reads "Save", not "check Save"
 *   href:    renders an <a> (via Radix `asChild`), as antd's Button does
 *   block:   w-full;  shape round/circle: rounded-full
 *
 * antd-only props (ghost/prefixCls/rootClassName/classNames/styles) are
 * stripped so they never reach the DOM.
 *
 * This seam MUST NOT re-emit antd's `.ant-btn*` class names, and on this
 * branch that is a live constraint rather than the reference branch's
 * historical note. antd is installed, every un-ported component still renders
 * antd Buttons inside itself, and antd v5's cssinjs injects a component's
 * rules the first time it renders — so `.ant-btn` is on the page whenever the
 * toggle is on. Putting it on OUR root would inherit antd's
 * height/border/background and visibly break every button. Select on
 * `data-slot="button"` / `data-variant` / `data-size` / `data-danger`
 * instead; those are the hooks the reference branch's own overrides use.
 * ------------------------------------------------------------------------- */

type ShadcnVariant = NonNullable<
  React.ComponentProps<typeof ShadcnButton>['variant']
>
type ShadcnSize = NonNullable<React.ComponentProps<typeof ShadcnButton>['size']>

export type AntdCompatButtonProps = ButtonProps & {
  /**
   * Force the icon-only footprint (square button, no label box). antd infers
   * this from "icon and no children"; the semantic button kit needs to say it
   * explicitly because it keeps the label around for the tooltip.
   */
  iconOnly?: boolean
}

const mapVariant = (
  type: ButtonProps['type'],
  danger?: boolean
): { variant: ShadcnVariant; dangerGhost: boolean } => {
  if (danger) {
    if (type === 'text' || type === 'link') {
      return { variant: 'ghost', dangerGhost: true }
    }
    return { variant: 'destructive', dangerGhost: false }
  }
  switch (type) {
    case 'primary':
      return { variant: 'default', dangerGhost: false }
    case 'text':
      return { variant: 'ghost', dangerGhost: false }
    case 'link':
      return { variant: 'link', dangerGhost: false }
    // antd 'dashed' and 'default' are both bordered/neutral → shadcn 'outline'
    default:
      return { variant: 'outline', dangerGhost: false }
  }
}

const mapSize = (size: ButtonProps['size'], iconOnly?: boolean): ShadcnSize => {
  if (iconOnly) {
    if (size === 'small') return 'icon-sm'
    if (size === 'large') return 'icon-lg'
    return 'icon'
  }
  if (size === 'large') return 'lg'
  if (size === 'small') return 'sm'
  return 'default'
}

const ButtonBase = React.forwardRef<HTMLButtonElement, AntdCompatButtonProps>(
  (props, ref) => {
    const {
      // antd props we translate
      type,
      danger,
      size,
      loading,
      icon,
      block,
      shape,
      htmlType,
      href,
      target,
      disabled,
      className,
      children,
      iconOnly,
      // antd-only props to strip (must never reach the DOM)
      ghost: _ghost,
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      classNames: _classNames,
      styles: _styles,
      ...domProps
    } = props

    const isLoading = !!loading
    // antd treats "an icon and nothing else" as the icon-only footprint.
    const isIconOnly =
      iconOnly ?? (!!icon && (children == null || children === false))
    const { variant, dangerGhost } = mapVariant(type, danger)
    const buttonSize = mapSize(size, isIconOnly)

    // Keep the icon element as the caller passed it (role="img" + aria-label,
    // via the lucide-backed `src/icons/antd-compat` wrapper), so whether it
    // joins the accessible name stays the CALLER's decision; the spinner
    // swapped in while loading is purely visual — the label and the disabled
    // state already carry the busy state — so it is always decorative.
    const iconNode = isLoading ? <LoadingOutlined spin aria-hidden /> : icon

    const mergedClassName = cn(
      block && 'w-full',
      (shape === 'round' || shape === 'circle') && 'rounded-full',
      type === 'dashed' && 'border-dashed',
      dangerGhost &&
        'text-destructive hover:bg-destructive/10 hover:text-destructive',
      className
    )

    // The hooks the rewritten CSS overrides select on. `data-slot`,
    // `data-variant` and `data-size` come from the shadcn Button itself; these
    // add back the two antd facets it has no concept of.
    const dataAttrs = {
      'data-danger': danger ? '' : undefined,
      'data-block': block ? '' : undefined,
      'data-loading': isLoading ? '' : undefined,
    }

    const content = (
      <>
        {iconNode}
        {children}
      </>
    )

    // antd Button with `href` renders an anchor — reproduce via asChild. A
    // disabled (or loading) link button drops its `href`, leaves the tab order
    // and stops taking pointer events, exactly as antd's does.
    if (href) {
      const isDisabled = !!disabled || isLoading
      return (
        <ShadcnButton
          asChild
          ref={ref as React.Ref<HTMLButtonElement>}
          variant={variant}
          size={buttonSize}
          className={cn(
            mergedClassName,
            isDisabled && 'pointer-events-none opacity-50'
          )}
          {...dataAttrs}
          {...domProps}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- a disabled link button is inert: no href, no tab stop, exactly as antd's is */}
          <a
            href={isDisabled ? undefined : href}
            target={target}
            aria-disabled={isDisabled || undefined}
            data-disabled={isDisabled ? '' : undefined}
            tabIndex={isDisabled ? -1 : undefined}
          >
            {content}
          </a>
        </ShadcnButton>
      )
    }

    return (
      <ShadcnButton
        ref={ref}
        variant={variant}
        size={buttonSize}
        type={htmlType ?? 'button'}
        disabled={disabled || isLoading}
        className={mergedClassName}
        {...dataAttrs}
        {...domProps}
      >
        {content}
      </ShadcnButton>
    )
  }
)
ButtonBase.displayName = 'Button'

/**
 * antd's `Button.Group` — a segmented row of buttons. Only the layout matters;
 * the repo has no live call site today, but the barrel must keep the shape
 * antd's own type declares.
 */
const ButtonGroup = ({
  className,
  size: _size,
  ...props
}: React.ComponentProps<'div'> & { size?: ButtonProps['size'] }) => (
  <div
    data-slot="button-group"
    className={cn(
      'inline-flex items-center [&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:last-child)]:border-r-0',
      className
    )}
    {...props}
  />
)

export const Button = Object.assign(ButtonBase, { Group: ButtonGroup })
